import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const STYLE = {
  version: 8,
  sources: {
    road: {
      type: "raster",
      tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
      maxzoom: 19,
      attribution: "Tiles &copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors",
    },
  },
  layers: [{ id: "road", type: "raster", source: "road" }],
};

const pinSvg = (color = "#FFC400") =>
  `<svg width="30" height="40" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 4px 6px rgba(0,0,0,.4))"><path d="M13 0C5.8 0 0 5.8 0 13c0 9 13 21 13 21s13-12 13-21C26 5.8 20.2 0 13 0z" fill="${color}"/><circle cx="13" cy="13" r="5" fill="#ffffff"/></svg>`;

export function StoreMap({ stores = [], center, userLoc }) {
  const pts = stores.filter((s) => s.lat != null && s.lng != null);
  const hasContent = pts.length > 0 || !!userLoc;

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [loaded, setLoaded] = useState(false);

  // init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const first = userLoc
      ? [userLoc[1], userLoc[0]]
      : center
      ? [center[1], center[0]]
      : pts.length
      ? [pts[0].lng, pts[0].lat]
      : [9.19, 45.46];
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      center: first,
      zoom: userLoc ? 17 : 13,
      pitch: 0,
      bearing: 0,
      antialias: true,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.scrollZoom.disable();
    map.on("load", () => { map.resize(); setLoaded(true); });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; setLoaded(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update markers + camera when data changes
  const ptsKey = JSON.stringify(pts.map((p) => [p.lat, p.lng, p.color]));
  const userKey = userLoc ? userLoc.join(",") : "";
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (userLoc) {
      const el = document.createElement("div");
      el.style.cssText =
        "width:18px;height:18px;border-radius:50%;background:#2E90FA;border:3px solid #fff;box-shadow:0 0 0 4px rgba(46,144,250,0.35)";
      const m = new maplibregl.Marker({ element: el })
        .setLngLat([userLoc[1], userLoc[0]])
        .setPopup(new maplibregl.Popup({ offset: 18 }).setHTML("<b>Sei qui</b>"))
        .addTo(map);
      markersRef.current.push(m);
    }

    pts.forEach((s) => {
      const el = document.createElement("div");
      el.innerHTML = pinSvg(s.color);
      el.style.cursor = "pointer";
      const html = `<div style="min-width:150px;font-family:inherit">
          <b>${s.name || ""}</b>
          <div style="font-size:12px;color:#555">${s.address || ""}</div>
          ${s.distance_km != null ? `<div style="font-size:12px;color:#c79400;font-weight:600">${s.distance_km} km</div>` : ""}
          <a href="/locale/${s.tenant_slug}/${s.store_slug}" style="color:#c79400;font-weight:600;font-size:13px">Vedi menu →</a>
        </div>`;
      const m = new maplibregl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([s.lng, s.lat])
        .setPopup(new maplibregl.Popup({ offset: 26 }).setHTML(html))
        .addTo(map);
      markersRef.current.push(m);
    });

    if (userLoc) {
      map.easeTo({ center: [userLoc[1], userLoc[0]], zoom: 17, pitch: 0, duration: 800 });
    } else if (pts.length === 1) {
      map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 15, pitch: 0, duration: 800 });
    } else if (pts.length > 1) {
      const b = new maplibregl.LngLatBounds();
      pts.forEach((s) => b.extend([s.lng, s.lat]));
      map.fitBounds(b, { padding: 60, maxZoom: 15, pitch: 0, duration: 800 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, ptsKey, userKey]);

  if (!hasContent) return null;
  return (
    <div
      ref={containerRef}
      className="rounded-2xl overflow-hidden border border-white/10 shadow-xl"
      style={{ height: 340 }}
      data-testid="ch-map"
    />
  );
}
